import React, { useRef, useEffect, useState } from 'react';
import { UnitTypeCountRow } from '../../types';
import { useFilters } from '../../cube/filters';

interface DonutUnitTypeProps {
  data: UnitTypeCountRow[];
}

export const DonutUnitType: React.FC<DonutUnitTypeProps> = ({ data }) => {
  const { filters, set } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(260);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const paddingLeft = parseFloat(computedStyle.paddingLeft);
        const paddingRight = parseFloat(computedStyle.paddingRight);
        const width = containerRef.current.offsetWidth - paddingLeft - paddingRight;
        setContainerWidth(Math.min(width, 320));
      }
    };
    
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);
  
  const total = data.reduce((s, d) => s + (Number(d.count) || 0), 0) || 1;
  const size = containerWidth; 
  const radius = size * 0.42; 
  const cx = size / 2; 
  const cy = size / 2;
  let acc = 0;
  const colors: Record<string, string> = { APARTMENT: '#FF5733', HOME: '#2979FF' };
  const labels: Record<string, string> = { APARTMENT: 'APARTAMENTO', HOME: 'CASA' };

  const hasActiveFilters = (filters.unitTypes || []).length > 0;
  
  const arcs = data.map((d) => {
    const value = Number(d.count) || 0;
    const angle = (value / total) * Math.PI * 2;
    const start = acc; const end = acc + angle; acc = end;
    const x1 = cx + radius * Math.sin(start); const y1 = cy - radius * Math.cos(start);
    const x2 = cx + radius * Math.sin(end); const y2 = cy - radius * Math.cos(end);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const label = `${labels[d.unit_type] || d.unit_type}: ${((value / total) * 100).toFixed(1)}% (${value})`;
    const isActive = (filters.unitTypes || []).includes(d.unit_type);
    const onClick = () => {
      const current = filters.unitTypes || [];
      const next = isActive ? current.filter((t) => t !== d.unit_type) : [...current, d.unit_type];
      set({ unitTypes: next.length ? next : undefined });
    };
    return { path, color: colors[d.unit_type] || '#64748b', label, isActive, onClick };
  });

  return (
    <div ref={containerRef} className="donut-chart-container" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <h3 style={{ textAlign: 'center' }}>Distribuição por tipo</h3>
      <div>
        <svg width={size} height={size}>
        {arcs.map((a, i) => (
          <path
            key={i}
            d={a.path}
            fill={a.color}
            opacity={hasActiveFilters ? (a.isActive ? 0.8 : 0.35) : 0.8}
            style={{ 
              cursor: 'pointer', 
              stroke: a.isActive ? '#fff' : 'none', 
              strokeWidth: a.isActive ? 3 : 0,
              transition: 'filter 200ms, transform 200ms',
              transformOrigin: `${cx}px ${cy}px`
            }}
            onClick={a.onClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.2)';
              e.currentTarget.style.transform = 'scale(1.01)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'brightness(1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <title>{a.label}</title>
          </path>
        ))}
        <circle cx={cx} cy={cy} r={radius * 0.75} fill="#171c23" stroke="#2d3748" />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontWeight={700} fill="#e2e8f0">
          {total}
        </text>
      </svg>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'center' }}>
        {data.map((d) => (
          <div key={d.unit_type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, background: colors[d.unit_type] || '#64748b' }} />
            {labels[d.unit_type] || d.unit_type} ({Number(d.count) || 0})
          </div>
        ))}
      </div>
    </div>
  );
};


